import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, tap } from 'rxjs';

import { API } from '../../../core/constants/api-endpoints';

/** Um alerta gerado pelo sistema durante o fechamento do Mês Base. */
export interface AlertaOperacional {
  idAlertaOperacional: number;
  idEmpreendimento: number;
  mesCompetencia: number;
  anoCompetencia: number;
  tipo: string;
  conta: string | null;
  titulo: string;
  mensagem: string;
  valorEsperado: number | null;
  valorEncontrado: number | null;
  diferenca: number | null;
  lido: boolean;
  dataLeitura: string | null;
  emailEnviado: boolean;
  dataCriacao: string;
}

export interface FiltroAlertas {
  idEmpreendimento?: number | null;
  mesReferencia?: number | null;
  anoReferencia?: number | null;
  apenasNaoLidos?: boolean;
}

/**
 * Alertas do fechamento.
 *
 * O contador de não lidos é um signal compartilhado: o sininho do topo e a
 * tela de Alertas leem o mesmo número, então marcar um alerta como lido na
 * tela apaga o badge na hora, sem esperar a próxima consulta.
 */
@Injectable({ providedIn: 'root' })
export class AlertasService {
  private readonly http = inject(HttpClient);

  private readonly _naoLidos = signal(0);
  readonly naoLidos = this._naoLidos.asReadonly();

  listar(filtro: FiltroAlertas = {}): Observable<AlertaOperacional[]> {
    let params = new HttpParams();

    if (filtro.idEmpreendimento) {
      params = params.set('idEmpreendimento', filtro.idEmpreendimento);
    }
    if (filtro.mesReferencia) {
      params = params.set('mesReferencia', filtro.mesReferencia);
    }
    if (filtro.anoReferencia) {
      params = params.set('anoReferencia', filtro.anoReferencia);
    }
    if (filtro.apenasNaoLidos) {
      params = params.set('apenasNaoLidos', true);
    }

    return this.http.get<AlertaOperacional[]>(API.operacoes.alertas.listar, { params });
  }

  /** Só a contagem - é o que o sininho consulta de tempos em tempos. */
  atualizarNaoLidos(): Observable<{ total: number }> {
    return this.http
      .get<{ total: number }>(API.operacoes.alertas.naoLidos)
      .pipe(tap((resposta) => this._naoLidos.set(resposta?.total ?? 0)));
  }

  marcarComoLido(idAlerta: number): Observable<void> {
    return this.http.post<void>(API.operacoes.alertas.marcarLido(idAlerta), {}).pipe(
      // Desconta na hora em vez de esperar a próxima leitura do servidor: o
      // badge some junto com o clique, que é o que o usuário espera ver.
      tap(() => this._naoLidos.update((n) => Math.max(0, n - 1)))
    );
  }
}
